import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Megaphone, Pin, User, Calendar, ArrowRight } from "lucide-react";

export default function StaffNoticesBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/staff-notices").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const goWork = () => {
    // Non-admin fallback destination based on role/permissions
    const perms = user?.permissions || [];
    const first = perms.find(p => p !== "/") || "/venda-rapida";
    navigate(first);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Olá, ${user?.name?.split(" ")[0] || "usuário"}!`}
        subtitle="Avisos da administração — confira antes de começar seu dia."
        actions={
          <Button onClick={goWork} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="board-continue">
            Ir pra trabalhar<ArrowRight className="w-4 h-4 ml-2"/>
          </Button>
        }
      />

      {loading ? (
        <div className="py-16 text-center text-slate-400">Carregando avisos...</div>
      ) : items.length === 0 ? (
        <Card className="p-16 text-center border-dashed border-2 border-slate-200" data-testid="board-empty">
          <Megaphone className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
          <h3 className="font-heading text-xl font-bold text-slate-700">Sem avisos no momento</h3>
          <p className="text-slate-500 mt-1">Bom trabalho! Clique acima pra começar seu turno.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="board-list">
          {items.map(n => (
            <Card key={n.id} className={`p-5 border-l-4 ${n.pinned ? "border-l-rose-500 bg-gradient-to-br from-rose-50 to-white shadow-md" : "border-l-slate-300"}`} data-testid={`board-notice-${n.id}`}>
              {n.pinned && (
                <div className="mb-2">
                  <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500 text-white font-bold uppercase tracking-widest inline-flex items-center gap-1"><Pin className="w-3 h-3"/>Fixado</span>
                </div>
              )}
              {n.image && (
                <img src={n.image} alt="" className="w-full h-auto object-contain rounded-lg mb-3 bg-slate-50"/>
              )}
              {n.title && <h3 className="font-heading text-xl font-black text-slate-900 mb-2">{n.title}</h3>}
              {n.body && <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{n.body}</p>}
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-3 pt-3 border-t border-slate-200">
                <span className="flex items-center gap-1"><User className="w-3 h-3"/>{n.created_by_name || "Admin"}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{new Date(n.created_at).toLocaleString("pt-BR")}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
